import React, { useEffect, useId, useRef } from "react";
import ReactDOM from "react-dom";
import "./dialogue.scss";

const FOCUSABLES =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Dialogue modal de la charte (remplace les modales Bootstrap jQuery) : carte
 * `surface` centrée, titre, bouton « Fermer », corps défilant, pied d'actions
 * facultatif sur `body`. Échap, le bouton et un clic hors de la carte ferment ;
 * le focus va au premier champ du corps, reste dans la carte, et revient à
 * l'élément qui avait le focus à l'ouverture.
 */
const Dialogue = ({ ouvert, titre, onFermer, children, actions = null, largeur = 560 }) => {
  const idTitre = "dialogue-titre-" + useId().replace(/:/g, "");
  const carteRef = useRef(null);
  const corpsRef = useRef(null);
  const fermerRef = useRef(onFermer);
  fermerRef.current = onFermer;

  useEffect(() => {
    if (!ouvert) return undefined;
    const precedent = document.activeElement;
    const premier =
      (corpsRef.current && corpsRef.current.querySelector(FOCUSABLES)) ||
      (carteRef.current && carteRef.current.querySelector(FOCUSABLES));
    if (premier) premier.focus();
    document.body.classList.add("dialogue-ouvert");
    return () => {
      document.body.classList.remove("dialogue-ouvert");
      if (precedent && typeof precedent.focus === "function" && document.contains(precedent)) {
        precedent.focus();
      }
    };
  }, [ouvert]);

  if (!ouvert) return null;

  const surTouche = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      fermerRef.current();
      return;
    }
    if (e.key !== "Tab" || !carteRef.current) return;
    const elements = Array.from(carteRef.current.querySelectorAll(FOCUSABLES));
    if (elements.length === 0) return;
    const premier = elements[0];
    const dernier = elements[elements.length - 1];
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    }
  };

  return ReactDOM.createPortal(
    <div
      className="dialogue"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) fermerRef.current();
      }}
    >
      <div
        ref={carteRef}
        className="dialogue__carte"
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitre}
        onKeyDown={surTouche}
        style={{ maxWidth: largeur }}
      >
        <div className="dialogue__entete">
          <h2 id={idTitre} className="dialogue__titre">
            {titre}
          </h2>
          <button type="button" className="dialogue__fermer" aria-label="Fermer" onClick={() => fermerRef.current()}>
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </div>
        <div ref={corpsRef} className="dialogue__corps">
          {children}
        </div>
        {actions && <div className="dialogue__pied">{actions}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Dialogue;
